import { useState, useEffect, forwardRef, useRef, } from 'react'

import './style.scss'
interface IAutocomplete {
  className?: string,
  id?: string,
  name?: string,
  placeholder?: string,
  suggestions: any,
  fullWidth?: boolean,
  tabIndex?: number,
  maxLength?: number,
  onChange?: (name: string, value: string) => void
}

type Ref = HTMLInputElement;

const Autocomplete = forwardRef<Ref, IAutocomplete>(({ className = '', id, name, suggestions, fullWidth = false, placeholder = '', tabIndex, maxLength, onChange }, ref) => {
  const autocompleteRef = useRef<HTMLDivElement>(null)
  const [filteredSuggestions, setFilteredSuggestions] = useState([])
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0)
  const [showSuggestions, setShowSuggestions] = useState(false)

  useEffect(() => {
    setFilteredSuggestions(suggestions)
  }, [suggestions])

  useEffect(() => {
    const checkIfClickedOutside = (event: any) => {
      if (showSuggestions && autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', checkIfClickedOutside)

    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside)
    }
  }, [showSuggestions])

  const onInputFocus = () => setShowSuggestions(true)

  const onInputKeyDown = (event: any) => {
    if (event.keyCode === 13) {
      setActiveSuggestionIndex(0)
      setShowSuggestions(false)

    } else if (event.keyCode === 38) {
      if (activeSuggestionIndex === 0) {
        return
      }

      setActiveSuggestionIndex(activeSuggestionIndex - 1)

    } else if (event.keyCode === 40) {
      if (activeSuggestionIndex - 1 === filteredSuggestions.length) {
        return
      }

      setActiveSuggestionIndex(activeSuggestionIndex + 1)
    }
  }

  const onInputChange = (event: any) => {
    const { value } = event.target

    const unLinked = suggestions.filter((suggestion: any) =>
      suggestion?.toLowerCase().indexOf(value?.toLowerCase()) > -1
    )

    setFilteredSuggestions(unLinked)
    setActiveSuggestionIndex(0)
    setShowSuggestions(true)
    if(onChange) onChange(name ? name : '', value)
  }

  const onItemClick = (event: any) => {
    const { innerText } = event.target

    setFilteredSuggestions([])
    setActiveSuggestionIndex(0)
    setShowSuggestions(false)
    if(onChange) onChange(name ? name : '', innerText)
  }

  const SuggestionsListComponent = () => {
    return filteredSuggestions.length ?
      <ul className="autocomplete_items">
        {filteredSuggestions.map((suggestion, index) => {
          let className

          // Flag the active suggestion with a class
          if (index === activeSuggestionIndex) {
            className = 'suggestion-active'
          }

          return (
            <li className={className} key={suggestion} onClick={onItemClick}>
              {suggestion}
            </li>
          )
        })}
      </ul>
    :
      null
  }

  return (
    <div ref={autocompleteRef} className={`autocomplete ${className} ${fullWidth ? 'w-full' : ''}`}>
      <input
        ref={ref}
        type="text"
        id={id}
        name={name}
        placeholder={placeholder}
        className={`input w-full`}
        tabIndex={tabIndex}
        maxLength={maxLength}
        onFocus={onInputFocus}
        onKeyDown={onInputKeyDown}
        onChange={onInputChange}
      />
      {showSuggestions && <SuggestionsListComponent />}
    </div>
  )
})

export { Autocomplete }